//!OLD PROMO BANNER CODE
//TODO add logic for toggle
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import {
    IBannerScheduleData,
    ICountdownScheduleData,
    IPromoBannerProps
} from '../definition-extensions/promo-banner.ext.props.autogenerated';
import { IMfrmHeaderData } from '../../../modules/mfrm-header/mfrm-header.data';
import { MFRMPencilBanner } from '../../../mfrm-components/mfrm-pencil-banner/MFRMPencilBanner';

// countdown component
import Countdown, { CountdownRenderProps } from 'react-countdown';

// used for date/time functionailty
import MsDyn365, { Link, ILinksData } from '@msdyn365-commerce/core';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(tz);
dayjs.extend(utc);
dayjs.extend(isBetween);
const format = 'M/D/YYYY H:m:s';

import { IPromoBannerViewProps } from '@msdyn365-commerce-modules/promo-banner';

// tslint:disable-next-line: max-func-body-length
const PromoBannerView: React.FC<IPromoBannerProps<IMfrmHeaderData> & IPromoBannerViewProps> = props => {
    const {
        promoBannerProps,
        promoBannerLinksProps,
        closeButton,
        carouselProps,
        config: { mfrmBannerMessages, bannerLimit, enableOOBPencilBanner }
    } = props;

    // VSI Customization - START

    const scheduledSlides: JSX.Element[] = [];
    // counter for the banner limit
    const adjustedBannerLimit = bannerLimit !== undefined ? bannerLimit : (mfrmBannerMessages && mfrmBannerMessages.length) || 0;
    let bannerCount = 0;

    mfrmBannerMessages &&
        mfrmBannerMessages.forEach((message, index) => {
            const bannersUnderLimit = adjustedBannerLimit - bannerCount;
            const bannerSchedule = message.bannerSchedule;

            // banner only follows schedule if enableBannerSchedule config is set to true
            const bannerShouldShow = bannerSchedule && bannerSchedule.enableBannerSchedule ? isScheduled(bannerSchedule) : true;

            // checks if banner is set to be enabled, is currently scheduled, and the banner limit hasn't been surpassed
            if (message.enableBanner && bannersUnderLimit && bannerShouldShow) {
                const countdownSchedule = message.countdownSchedule;
                bannerCount++;

                // add countdown to richText if required
                const parsedRichText = message.richText && ReactHtmlParser(message.richText.toString());

                // add countdown component into richText
                countdownSchedule && parsedRichText && countdownSchedule.enableCountdown && setCountDown(countdownSchedule, parsedRichText);

                const editableLink: ILinksData = {
                    ariaLabel: message.links![0].ariaLabel,
                    className: 'ms-promo-banner__link',
                    linkText: message.links![0].linkText,
                    openInNewTab: message.links![0].openInNewTab,
                    role: 'link',
                    linkUrl: message.links![0].linkUrl.destinationUrl
                };

                const promoBannerParentDivRef = React.useRef<any>();
                React.useEffect(() => {
                    promoBannerParentDivRef.current &&
                        promoBannerParentDivRef.current.addEventListener('click', function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
                            event.preventDefault();
                            if ('utag' in window) {
                                const eventLabel = `${htmltoText(message.richText?.toString()!).trim()}`;
                                // @ts-ignore
                                utag.link({
                                    event: 'pencil banner tracking',
                                    event_category: 'pencil banner tracking',
                                    event_action: 'pencil banner click',
                                    event_label: eventLabel,
                                    event_noninteraction: 'false'
                                });
                            }
                            if (MsDyn365.isBrowser) {
                                window.location.href = message.links![0].linkUrl.destinationUrl;
                            }
                        });
                }, []);

                scheduledSlides.push(
                    <a href={editableLink.linkUrl} className='msc-carousel__anchor' data-testid='BannerItem'>
                        <Node ref={promoBannerParentDivRef} className={`ms-promo-banner__content`} key={index} >
                            {message.richText && <div className={`ms-promo-banner__rich-text-${index}`}>{parsedRichText}</div>}
                            {message.links && message.links.length > 0 && <Link {...promoBannerLinksProps} link={editableLink} />}
                        </Node>
                    </a>
                );
            }
        });

    // override the _next() and _prev() functions from promo-banner.tsx to only count scheduled slides instead of all slides when determining first and last slides
    carouselProps.next = (): void => {
        const isLastItem = props.activeIndex === scheduledSlides.length - 1;
        const nextIndex = isLastItem ? 0 : props.activeIndex + 1;
        // sets the index for the next button. equivalent to _goToIndex() from promo-banner.tsx
        carouselProps.onIndicatorsClickHandler(nextIndex);
    };

    carouselProps.previous = (): void => {
        const isFirstItem = props.activeIndex === 0;
        const nextIndex = isFirstItem ? (mfrmBannerMessages ? scheduledSlides.length - 1 : 0) : props.activeIndex - 1;
        carouselProps.onIndicatorsClickHandler(nextIndex);
    };
    if (enableOOBPencilBanner) {
        return (
            <Module {...promoBannerProps}>
                <Node {...carouselProps} items={MsDyn365.isBrowser ? scheduledSlides : <></>} data-testid='BannerScrollArrow' />
                {closeButton}
            </Module>
        );
    }
    return (
        <div className='row'>
            <MFRMPencilBanner {...props} />
        </div>
    );
};

// strips content inside given element
const clearContent = (parsedRichText: React.ReactElement[]): void => {
    // find countdown element and clear its content
    for (let i = 0; i < parsedRichText.length; i++) {
        const element = parsedRichText[i];
        const classNames = element.props.className && element.props.className.split(' ');
        if (classNames && classNames.includes('countdown')) {
            element.props.children.length = 0;
            return;
        }
    }
};

// adds Countdown component as a child of the element with the id 'countdown' is countdown should show
const setCountDown = (countdownSchedule: ICountdownScheduleData, parsedRichText: React.ReactElement[]): void => {
    // check if countdown is scheduled to show
    if (!isScheduled(countdownSchedule)) {
        return;
    }

    // clear content inside 'countdown' element
    clearContent(parsedRichText);

    const endDate = countdownSchedule.endDate;
    const endTime = countdownSchedule.endTime;
    const scheduleEnd = dayjs(`${endDate} ${endTime}`, format);
    const scheduleEndLocal = scheduleEnd && convertToLocal(scheduleEnd, countdownSchedule);

    const renderer = ({ formatted }: CountdownRenderProps) => {
        return (
            <span>
                <span className='hour'>{formatted.hours}</span>:<span className='minute'>{formatted.minutes}</span>:
                <span className='second'>{formatted.seconds}</span>
            </span>
        );
    };

    const countdown = scheduleEndLocal && <Countdown date={scheduleEndLocal.toDate()} daysInHours={true} renderer={renderer} />;

    // find target and put countdown inside of it
    parsedRichText.forEach(element => {
        const classNames = element.props.className && element.props.className.split(' ');
        if (classNames && classNames.includes('countdown')) {
            element.props.children.push(countdown);
            return;
        }
    });
};

// checks if countdown/banner is currently scheduled
const isScheduled = (schedule: ICountdownScheduleData | IBannerScheduleData): Boolean => {
    let start = schedule.startDate && schedule.startTime && dayjs(`${schedule.startDate} ${schedule.startTime}`, format);
    let end = schedule.endDate && schedule.endTime && dayjs(`${schedule.endDate} ${schedule.endTime}`, format);

    // check if schedule is valid
    const validStart = start && start.isValid();
    const validEnd = end && end.isValid();

    if (!validStart || !validEnd) {
        return false;
    }

    const now = dayjs();

    // convert to local time
    start = start && convertToLocal(start, schedule);
    end = end && convertToLocal(end, schedule);

    return now.isBetween(start, end);
};

// convert time to local time
const convertToLocal = (time: dayjs.Dayjs, schedule: ICountdownScheduleData | IBannerScheduleData): dayjs.Dayjs | undefined => {
    const timeZone = schedule.timeZone && getTimeZone(schedule.timeZone);

    return !timeZone
        ? time
        : dayjs(time.format(format), format)
            .tz(timeZone)
            .local();
};

// convert timezone choice to matching timezone in moment-timezone
const getTimeZone = (timeZone: string): string | undefined => {
    switch (timeZone) {
        case 'hawaii':
            return 'America/Adak';
        case 'alaska':
            return 'America/Anchorage';
        case 'pacific':
            return 'America/Los_Angeles';
        case 'mountain':
            return 'America/Denver';
        case 'central':
            return 'America/Chicago';
        case 'eastern':
            return 'America/New_York';
        default:
            return undefined;
    }
};

const htmltoText = (html: string) => {
    if (MsDyn365.isBrowser) {
        // Create a new div element
        const tempDivElement = document.createElement('div');

        // Set the HTML content with the given value
        // tslint:disable-next-line: no-inner-html
        tempDivElement.innerHTML = html;

        // Retrieve the text property of the element
        return tempDivElement.textContent || tempDivElement.innerText || '';
    } else {
        return '';
    }
};

// VSI Customization - END

export default PromoBannerView;
